import { Button, Dialog, Flex, Select, TextInput, useToasts } from '@applyboard/crystal-ui';
import { EditOutlineIcon } from '@applyboard/ui-icons';
import { useState, Fragment } from 'react';
import { useErrorHandler } from '~/hooks';
import { useAgencies } from '~/pages/Applications/data/useAgencies';
import { useAppProcessing } from '../../context';
import { useChangeAgency } from '../../hooks/useChangeAgency';

export function ChangeAgency() {
  const { application } = useAppProcessing();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [currentAgency, setCurrentAgency] = useState(application.agency?.agencyName ?? '');

  const { changeAgency, isPatchingAgency } = useChangeAgency(application.id);

  const toast = useToasts();
  const { apiError } = useErrorHandler();

  const onSave = async () => {
    try {
      await changeAgency(selectedAgency);
      toast.positive('Agency has been successfully changed.');
      setSelectedAgency('');
      setIsOpen(false);
      setTimeout(() => {
        window.location.href = `/applications/${application.id}`;
      }, 1000);
    } catch (err) {
      apiError();
    }
  };

  return (
    <>
      <Button
        intent="primary"
        emphasis="transparent"
        aria-label="Open menu"
        onClick={() => setIsOpen(true)}
        leadIcon={EditOutlineIcon}
      >
        Change agency
      </Button>

      <Dialog
        open={isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) setIsOpen(false);
        }}
      >
        <Dialog.Heading>Change Agency</Dialog.Heading>
        <Dialog.Content>
          <Flex gap={4} direction="column">
            <TextInput
              label="Current agency"
              onChange={setCurrentAgency}
              value={currentAgency}
              aria-label="Current agency"
              disabled
            />
            <SelectAgency selectedAgency={selectedAgency} setSelectedAgency={setSelectedAgency} />
          </Flex>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
          <Button
            intent="primary"
            width="fill"
            disabled={!selectedAgency || isPatchingAgency}
            onClick={() => {
              onSave();
            }}
            loading={isPatchingAgency}
          >
            Confirm change
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
}

interface SelectAgencyProps {
  selectedAgency: string;
  setSelectedAgency: React.Dispatch<React.SetStateAction<string>>;
}

const SelectAgency = ({ selectedAgency, setSelectedAgency }: SelectAgencyProps) => {
  const { application } = useAppProcessing();
  const { agencies } = useAgencies({ 'page[size]': 1000, 'page[number]': 1 });

  return (
    <Select
      label="New agency"
      required
      onChange={(value) => setSelectedAgency(value)}
      appearance="styled"
      value={selectedAgency}
      aria-label="agency"
    >
      {agencies.length > 0
        ? agencies.map((program) => (
            <Fragment key={program.id}>
              {application.agency?.id !== program.id ? <Select.Option label={program.name} value={program.id} /> : null}
            </Fragment>
          ))
        : null}
    </Select>
  );
};
